import {
  apiService,
  extractSyncResponse,
  getModelCount,
  IAdministrator,
  IAllCoachProfile,
  IAreaEducationOfficer,
  ICoachProfileV1,
  IFeatures,
  IPrincipalProfileV1,
  IProfileLabels,
  IProgramManager,
  IRegionalManager,
  ISchools,
  ISchoolGroups,
  ISchoolRegions,
  IStudentProfile,
  ISyncChangesResponse,
  ITeacherProfileV1,
  ITransferUserProfile,
  IUser,
  IUserRolesAndPermissions,
  userDB,
} from '..';

const PAGE_SIZE = 1000;

interface IProfileResponse {
  timestamp: string;
  teacherProfiles: ISyncChangesResponse<ITeacherProfileV1>;
  principalProfiles: ISyncChangesResponse<IPrincipalProfileV1>;
  coachProfiles: ISyncChangesResponse<ICoachProfileV1>;
  allCoachProfiles: ISyncChangesResponse<IAllCoachProfile>;
  areaEducationOfficerProfiles: ISyncChangesResponse<IAreaEducationOfficer>;
  regionalManagerProfiles: ISyncChangesResponse<IRegionalManager>;
  programManagerProfiles: ISyncChangesResponse<IProgramManager>;
  administratorProfiles: ISyncChangesResponse<IAdministrator>;
  studentProfiles: ISyncChangesResponse<IStudentProfile>;
  users: ISyncChangesResponse<IUser>;
  transferUserProfiles: ISyncChangesResponse<ITransferUserProfile>;
}

interface IGetRoleAndPemissionResponse {
  timestamp: string;
  userRoles: ISyncChangesResponse<IUserRolesAndPermissions>;
  features: ISyncChangesResponse<IFeatures>;
  profileLabels: ISyncChangesResponse<IProfileLabels>;
}
export const fetchRolesAndPermission = async (
  timestamp: string | null,
  pageNumber: number = 1,
): Promise<boolean> => {
  const response = await apiService.get<IGetRoleAndPemissionResponse>(
    `/api/v2/sync-user-roles/?page_size=${PAGE_SIZE}&page=${pageNumber}&c=${Date.now()}&${
      timestamp ? `last_pulled_at=${timestamp}` : ''
    }`,
  );
  const data = response.data;

  console.log(`Fetched roles and permission data for page ${pageNumber}`, data);
  let lastResult = true;
  const models = ['userRoles', 'features', 'profileLabels'] as const;

  try {
    const addDataToDb = await extractSyncResponse(models, data, userDB);
    console.log(`Added ${models} in db ${addDataToDb}`);
  } catch (e) {
    lastResult = false;
    console.error(`Unable to add data for ${models} ${e}`);
  }

  const modelCounts = getModelCount(models, data);
  const maxPages = Math.max(...modelCounts) / PAGE_SIZE;

  if (pageNumber <= maxPages) {
    // fetch the next page.
    lastResult =
      lastResult && (await fetchRolesAndPermission(timestamp, pageNumber + 1));
  }

  return lastResult;
};

export const fetchProfiles = async (
  timestamp: string | null,
  pageNumber = 1,
): Promise<boolean> => {
  const response = await apiService.get<IProfileResponse>(
    `/api/v1/sync-user-profiles/?page_size=${PAGE_SIZE}&page=${pageNumber}&c=${Date.now()}&${
      timestamp ? `last_pulled_at=${timestamp}` : ''
    }`,
  );

  const data = response.data;

  // Map API keys to model names with V2 suffix where needed
  const mappedData = {
    ...data,
    teacherProfilesV2: data.teacherProfiles,
    principalProfilesV2: data.principalProfiles,
    coachProfilesV2: data.coachProfiles,
    areaEducationOfficerProfilesV2: data.areaEducationOfficerProfiles,
    regionalManagerProfilesV2: data.regionalManagerProfiles,
    programManagerProfilesV2: data.programManagerProfiles,
    administratorProfilesV2: data.administratorProfiles,
    studentProfilesV2: data.studentProfiles,
    usersV1: data.users,
    transferUserProfiles: data.transferUserProfiles,
  };

  let lastResult = true;
  const models = [
    'teacherProfilesV2',
    'principalProfilesV2',
    'coachProfilesV2',
    'allCoachProfilesV2',
    'areaEducationOfficerProfilesV2',
    'regionalManagerProfilesV2',
    'programManagerProfilesV2',
    'administratorProfilesV2',
    'studentProfilesV2',
    'usersV1',
    'transferUserProfiles',
  ] as const;

  try {
    await extractSyncResponse(models, mappedData, userDB);
  } catch (e) {
    lastResult = false;
    console.error(`Unable to add data for ${models}`, e);
  }

  const modelCounts = getModelCount(models, mappedData);
  const maxPages = Math.max(...modelCounts) / PAGE_SIZE;

  if (pageNumber <= maxPages) {
    lastResult = lastResult && (await fetchProfiles(timestamp, pageNumber + 1));
  }

  return lastResult;
};

interface IGetSchoolGroupsResponse {
  timestamp: string;
  schools: ISyncChangesResponse<ISchools>;
  schoolRegions: ISyncChangesResponse<ISchoolRegions>;
  schoolGroups: ISyncChangesResponse<ISchoolGroups>;
}
export const fetchSchoolsGroups = async (
  timestamp: string | null,
  pageNumber = 1,
): Promise<boolean> => {
  const response = await apiService.get<IGetSchoolGroupsResponse>(
    `/api/v1/sync-schools-and-groups/?page_size=${PAGE_SIZE}&page=${pageNumber}&c=${Date.now()}&${
      timestamp ? `last_pulled_at=${timestamp}` : ''
    }`,
  );

  const data = response.data;

  // Transform schools data to ensure uuid exists (backend should send both uuid and id)
  // Fallback to generate uuid from id if missing (defensive programming)
  const transformedSchools = {
    created: {
      ...data.schools.created,
      data: data.schools.created.data.map((school: ISchools) => ({
        ...school,
        uuid: school.uuid || `school-${school.id}`, // Fallback if backend doesn't send uuid
      })),
    },
    updated: {
      ...data.schools.updated,
      data: data.schools.updated.data.map((school: ISchools) => ({
        ...school,
        uuid: school.uuid || `school-${school.id}`,
      })),
    },
    deleted: data.schools.deleted,
  };

  // Map schools to schoolsV1 following the V1 pattern
  const mappedData = {
    ...data,
    schoolsV1: transformedSchools,
  };

  let lastResult = true;
  const models = ['schoolsV1', 'schoolGroups', 'schoolRegions'] as const;

  try {
    await extractSyncResponse(models, mappedData, userDB);
  } catch (e) {
    lastResult = false;
    console.error(`Unable to add data for ${models}`, e);
  }

  const modelCounts = getModelCount(models, mappedData);
  const maxPages = Math.max(...modelCounts) / PAGE_SIZE;

  if (pageNumber <= maxPages) {
    lastResult =
      lastResult && (await fetchSchoolsGroups(timestamp, pageNumber + 1));
  }

  return lastResult;
};

interface IGetAllCoacherProfilesResponse {
  timestamp: string;
  allCoachesProfiles: ISyncChangesResponse<IAllCoachProfile>;
}
export const fetchAllCoachProfiles = async (
  timestamp: string | null,
  pageNumber = 1,
): Promise<boolean> => {
  const response = await apiService.get<{
    timestamp: string;
    coachProfiles: ISyncChangesResponse<IGetAllCoacherProfilesResponse>;
  }>(
    `/api/v1/sync-all-coach-profiles/?page_size=${PAGE_SIZE}&page=${pageNumber}&c=${Date.now()}&${
      timestamp ? `last_pulled_at=${timestamp}` : ''
    }`,
  );

  const data = response.data;
  const mappedData = {
    ...data,
    allCoachProfilesV2: data.coachProfiles,
  };

  let lastResult = true;
  const models = ['allCoachProfilesV2'] as const;

  try {
    const addDataToDb = await extractSyncResponse(models, mappedData, userDB);
    console.log(`Added ${models} in db ${addDataToDb}`);
  } catch (e) {
    lastResult = false;
    console.error(`Unable to add data for ${models} ${e}`);
  }

  const modelCounts = getModelCount(models, data);
  const maxPages = Math.max(...modelCounts) / PAGE_SIZE;

  if (pageNumber <= maxPages) {
    // fetch the next page.
    lastResult =
      lastResult && (await fetchAllCoachProfiles(timestamp, pageNumber + 1));
  }

  return lastResult;
};
